<template>
  <!-- Sticky: the bar stays pinned to the top of the viewport while the page
       content scrolls underneath it. Styled like the web account-area header
       (torik.ai/account) — flat surface, one hairline border, brand on the left. -->
  <header
    class="sticky top-0 z-40 pt-safe border-b border-gray-200 bg-white/90 backdrop-blur-xl dark:border-gray-800 dark:bg-gray-900/90">
    <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <div class="flex h-16 items-center justify-between gap-4">
        <!-- Left: brand + the area this page belongs to -->
        <div class="flex min-w-0 items-center gap-2">
          <div
            class="h-9 w-9 flex-shrink-0 rounded-full bg-cover bg-center shadow-md"
            :style="`background-image: url('${systemImage || ottoAppIcon}')`"></div>
          <span class="ml-1 hidden text-lg font-bold tracking-tight text-gray-800 dark:text-gray-200 sm:inline">
            {{ brand.platformCompact }}
          </span>
          <h1 class="truncate text-lg font-bold tracking-tight text-primary-600 dark:text-primary-400">
            {{ tt('Servers', 'Strežniki') }}
          </h1>
        </div>

        <!-- Right: the services launcher, then the account chip. The web
             account-area header carries no other chrome, so neither does this. -->
        <div class="flex flex-shrink-0 items-center gap-2">
          <!-- Services launcher — the Torik Cloud portals this hub works with -->
          <button
            type="button"
            @click="emit('openServices')"
            class="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white/70 py-2 pl-2 pr-3 text-sm font-medium text-gray-700 transition-colors hover:bg-white hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-white"
            :title="tt('Services', 'Storitve')">
            <span class="material-symbols-outlined text-xl">apps</span>
            <span class="hidden sm:inline">{{ tt('Services', 'Storitve') }}</span>
          </button>

          <!-- Account chip (signed in) -->
          <button
            v-if="isCloudAuthenticated && cloudUser"
            type="button"
            @click="emit('openMenu')"
            class="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-white/70 py-1.5 pl-1.5 pr-3 text-sm font-medium text-gray-800 transition-colors hover:bg-white dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:hover:bg-gray-800"
            :title="tt('Account menu', 'Meni računa')">
            <span
              class="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-400 text-sm font-bold uppercase text-gray-900">
              <img
                v-if="cloudUser.image"
                :src="cloudUser.image"
                :alt="cloudUser.name || tt('User', 'Uporabnik')"
                class="h-full w-full object-cover"
                @error="
                  (e: Event) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }
                " />
              <span v-else>{{ userInitials }}</span>
            </span>
            <span class="hidden max-w-[10rem] truncate sm:inline">
              {{ cloudUser.name || cloudUser.email }}
            </span>
            <span class="material-symbols-outlined text-base text-gray-500 dark:text-gray-400">expand_more</span>
          </button>

          <!-- Signed out: the drawer holds the sign-in action -->
          <button
            v-else
            type="button"
            @click="emit('openMenu')"
            class="rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-primary-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-200 dark:hover:text-white"
            :title="tt('Sign in', 'Prijava')">
            {{ tt('Sign in', 'Prijava') }}
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { brand } from '../../config/brand';
import { tt } from '../../utils/consoleText';

interface CloudUser {
  id: string;
  email: string;
  name?: string;
  image?: string;
}

const props = defineProps<{
  cloudUser: CloudUser | null;
  isCloudAuthenticated: boolean;
  /** Location branding image (blob URL) when connected to a hub */
  systemImage?: string;
}>();

const emit = defineEmits<{
  openMenu: [];
  openServices: [];
}>();

// App icon. Base-relative so it resolves under Electron's file:// origin
// (built with base "./") as well as the web build (base "/").
const ottoAppIcon = `${import.meta.env.BASE_URL}${brand.images.logoHeader.replace(/^\//, '')}`;

const userInitials = computed(() => {
  if (props.cloudUser?.name) {
    const parts = props.cloudUser.name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return props.cloudUser.name.substring(0, 2).toUpperCase();
  }
  if (props.cloudUser?.email) {
    return props.cloudUser.email.substring(0, 2).toUpperCase();
  }
  return '?';
});
</script>
